// Daily Garden gamification state. The server (`/api/v1/garden`) is the source
// of truth; we keep a localStorage MIRROR so synchronous reads (e.g. the exam
// header) stay instant. `recordWatering` optimistically updates the mirror and
// fires the authoritative POST; the next fetch reconciles.
import { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPost } from '@/lib/api';
import { studentId } from '@/hooks/useTaskCards';

export interface GardenState {
  streak: number;
  longestStreak: number;
  lastActive: string | null; // YYYY-MM-DD
  todayXP: number;
  history: string[]; // active day-keys
}

export const DAILY_GOAL = 100;

const EMPTY: GardenState = { streak: 0, longestStreak: 0, lastActive: null, todayXP: 0, history: [] };

const key = (): string => `cwy_garden_${studentId() || 'anon'}`;
const dayKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function writeMirror(s: GardenState): void {
  try { localStorage.setItem(key(), JSON.stringify(s)); } catch { /* ignore quota */ }
}

/** Synchronous read of the local mirror (last known server state). */
export function readGarden(): GardenState {
  try {
    const raw = localStorage.getItem(key());
    if (!raw) return EMPTY;
    return { ...EMPTY, ...(JSON.parse(raw) as Partial<GardenState>) };
  } catch {
    return EMPTY;
  }
}

interface GardenApiDTO extends GardenState { dailyGoal: number }

/** Fetch authoritative state from the API and refresh the mirror. */
export async function fetchGarden(): Promise<GardenState> {
  const dto = await apiGet<GardenApiDTO>('/garden');
  const state: GardenState = {
    streak: dto.streak, longestStreak: dto.longestStreak,
    lastActive: dto.lastActive, todayXP: dto.todayXP, history: dto.history ?? [],
  };
  writeMirror(state);
  return state;
}

/**
 * Record a completed set. Optimistically advances the local mirror with the
 * same rules the server uses, fires the authoritative POST, and returns the
 * optimistic state synchronously (callers may use it immediately).
 */
export function recordWatering(xp: number): GardenState {
  const prev = readGarden();
  const today = dayKey(new Date());
  const yesterday = dayKey(new Date(Date.now() - 864e5));

  let streak = prev.streak;
  let todayXP = prev.todayXP;
  if (prev.lastActive === today) {
    todayXP += xp;
  } else {
    streak = prev.lastActive === yesterday ? prev.streak + 1 : 1;
    todayXP = xp;
  }
  streak = Math.max(streak, 1);
  const history = prev.history.includes(today) ? prev.history : [...prev.history, today].slice(-28);
  const optimistic: GardenState = {
    streak, longestStreak: Math.max(prev.longestStreak, streak), lastActive: today, todayXP, history,
  };
  writeMirror(optimistic);

  // Authoritative write — reconcile the mirror with the server's response.
  void apiPost<GardenApiDTO>('/garden/water', { xp })
    .then((dto) => writeMirror({
      streak: dto.streak, longestStreak: dto.longestStreak,
      lastActive: dto.lastActive, todayXP: dto.todayXP, history: dto.history ?? [],
    }))
    .catch(() => { /* offline — mirror keeps the optimistic value */ });

  return optimistic;
}

/** Mon→Sun booleans for the current week, from the activity history. */
export function weekActivity(state: GardenState): boolean[] {
  const set = new Set(state.history);
  if (state.lastActive) set.add(state.lastActive);
  const now = new Date();
  const monday = new Date(now);
  const dow = (now.getDay() + 6) % 7; // 0 = Monday
  monday.setDate(now.getDate() - dow);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return set.has(dayKey(d));
  });
}

/** Hook: seeds from the mirror, then syncs from the API on mount. */
export function useGarden(): { state: GardenState; water: (xp: number) => void } {
  const [state, setState] = useState<GardenState>(() => readGarden());
  useEffect(() => {
    fetchGarden().then(setState).catch(() => { /* keep mirror */ });
  }, []);
  const water = useCallback((xp: number) => setState(recordWatering(xp)), []);
  return { state, water };
}
